import { useTranslation } from 'react-i18next';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import CircleLoader, {
  Types as CircleLoaderTypes,
} from '@lib/components/CircleLoader/CircleLoader';
import useDocReference from './hooks/useDocReference';
import InlineDocChip from './InlineDocChip';
import DocChip from './DocChip';
import {
  getCitationForVersion,
  VerifiedCitationMoments,
  VerifiedCitationPages,
} from './utils/citationPages';
import styles from './AiAssistant.module.scss';

type Props = {
  // Provided by the remark transform via hProperties.
  id?: string;
  // The version the assistant pinned (`version-id` directive attribute); resolves
  // the chip to that exact version's file/notes instead of the current version.
  versionId?: string;
  inline?: boolean;
  // True while the assistant turn is still streaming — render a placeholder and
  // skip the fetch until the message is persisted.
  isStreaming?: boolean;
  // Pages of this reference the message's `citations` verified, and the
  // version they belong to (BE guide §5).
  citation?: VerifiedCitationPages | null;
  // Video moments of this reference the message's `citations` verified.
  moments?: VerifiedCitationMoments | null;
};

// Renders an AI `:documentation{id}` / `::documentation{id}` reference. The
// inline form (text directive) must stay inline-level because it lives inside a
// paragraph, so it renders a chip; the block form (leaf directive) renders the
// full DocumentationPreview card.
function DocReference({
  id = '',
  versionId,
  inline = false,
  isStreaming = false,
  citation,
  moments,
}: Props) {
  const { t } = useTranslation();
  const { loading, error, documentation } = useDocReference(id, {
    skip: isStreaming,
    versionId,
  });

  const attachment = documentation?.currentVersion?.attachmentAttached;
  const isPending = isStreaming || loading;
  const isUnavailable = !isPending && (error || !documentation || !attachment);

  if (inline) {
    // Resolved reference: full chip with the trailing actions menu.
    if (!isPending && !isUnavailable && documentation) {
      // Pages and times only mean something in the file they were read from:
      // show them only when the chip resolved to the cited version (a pinned
      // version that failed to load falls back to the current one).
      const resolvedVersionId = documentation.currentVersion?.id;
      return (
        <InlineDocChip
          documentation={documentation}
          citation={getCitationForVersion(citation, resolvedVersionId)}
          moments={getCitationForVersion(moments, resolvedVersionId)?.moments}
        />
      );
    }
    // Pending: the same chip as the resolved one, with a loader standing in for
    // the actions menu — resolving then swaps the trailing control and nothing
    // else. Unavailable is the only muted, trailing-less state.
    return (
      <DocChip
        muted={!!isUnavailable}
        label={isPending ? t('document') : t('document-unavailable')}
        trailing={
          isPending ? (
            <span className={styles.docRefChipLoader}>
              <CircleLoader
                size={16}
                padding={0}
                type={CircleLoaderTypes.GIF}
              />
            </span>
          ) : undefined
        }
      />
    );
  }

  if (isPending || isUnavailable) {
    return (
      <div className={styles.docRefCardPlaceholder}>
        <MaterialIcon icon="description" symbolsOutlined size="md-20" />
        <Typography variant="caption">
          {isPending ? t('document') : t('document-unavailable')}
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.docRefBlock}>
      <DocumentationPreview
        attachment={attachment}
        notes={documentation?.currentVersion?.notes || undefined}
        isFavorite={!!documentation?.favorited}
        isUnread={!documentation?.read}
        view={DocumentationPreviewEnum.DocumentRowCard}
      />
    </div>
  );
}

export default DocReference;
